/**
 * A bare `MediaStreamTrack` that only Safari's picture-in-picture video shows, shaped like the
 * `TrackReference` the renderer already knows how to attach and detach.
 *
 * Two pictures use it, both local to this device and never published: our own live camera kept through a
 * None-to-effect hold (`holdPreviewTrackRef`, SC-158) and the neutral picture shown for a held or missing
 * camera (`neutralPicture`, SC-476).
 *
 * It does nothing to the element itself. The renderer puts `mediaStreamTrack` on the real stream
 * (`PipElementSource.show`), the way it does for LiveKit's own tracks, so `attach` and `detach` here are only
 * the hooks a picture needs (a canvas that must draw as it goes on).
 */

import type { TrackReference } from '@livekit/components-react'

export interface StreamTrackHooks {
  /** Runs as the track goes on the element, e.g. to make sure a canvas has drawn a frame for it. */
  onAttach?: (element: HTMLMediaElement) => void
  onDetach?: (element: HTMLMediaElement) => void
}

/** The methods the renderer calls on any track it shows. */
export interface AttachableTrack {
  attach: (element: HTMLMediaElement) => void
  detach: (element: HTMLMediaElement) => void
  /** What goes on the element. LiveKit's tracks and ours both have one. */
  mediaStreamTrack?: MediaStreamTrack
}

export function streamTrackRef(
  media: MediaStreamTrack,
  hooks: StreamTrackHooks = {}
): TrackReference {
  const track: AttachableTrack = {
    mediaStreamTrack: media,
    attach(element) {
      hooks.onAttach?.(element)
    },
    detach(element) {
      hooks.onDetach?.(element)
    },
  }
  return { publication: { track } } as unknown as TrackReference
}
